import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import type { ColumnSortEvent } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { FilterMatchMode } from "primereact/api";
import { subDays, subMonths, subYears } from "date-fns";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useSelfInitiatedAssessments, useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { humanDateWithoutTime, humanEndDate } from "sj-ui-toolkit/src/utils/dates";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { getChoresClient, getKnowledgeAssessmentReportClient } from "sj-ui-toolkit/src/client";

import CategoriesCarousel from "@/components/reports/assessments/CategoriesCarousel";
import { STATUS_CONFIG, type KaStatusKey } from "sj-ui-toolkit/src/constants/assessmentStatus";
import {
  DATE_RANGE_OPTIONS,
  type DateRangeValue,
  STATUS_CODE_MAP,
  type CategoryScore,
  type TopicScore,
  type LearnerScore,
} from "@/constants/assessmentsReport.constants";
import { filterAllScores, calculateAverageScores } from "@/helpers/assessmentsReport";
import { withSkeleton, skeletonRows, statusBody } from "@/helpers/tableBody";
import { textFilterElement, multiSelectFilterElement } from "@/helpers/tableFilters";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssessmentReport {
  id: string;
  title: string;
  startDate: Date | null;
  endDate: Date | "Dynamic" | null;
  totalAssigned: number;
  totalCompleted: number;
  totalInProgress: number | null;
  statusCode: string;
  statusName: string;
  selfInitiated?: boolean;
  selfInitiatedPath?: string;
}

interface StatusOption {
  code: string;
  displayName: string;
}

interface AssessmentsReportState {
  assessments: AssessmentReport[] | "loading";
  statusOptions: StatusOption[];
  categoryScores: CategoryScore[];
  topicScores: Record<string, TopicScore[]>;
  isCategoriesLoading: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDateRangeCutoff = (rangeValue: DateRangeValue): Date | null => {
  if (rangeValue === "all") return null;
  const now = new Date();
  switch (rangeValue) {
    case "7days":
      return subDays(now, 7);
    case "30days":
      return subDays(now, 30);
    case "90days":
      return subDays(now, 90);
    case "6months":
      return subMonths(now, 6);
    case "1year":
      return subYears(now, 1);
    default:
      return null;
  }
};

const mapAssessmentToReport = (report: Record<string, unknown>): AssessmentReport => ({
  id: report.uuid as string,
  title: report.title as string,
  startDate: report.startDate ? new Date(report.startDate as string) : null,
  endDate: report.endDate
    ? new Date(report.endDate as string)
    : (report.dynamic as boolean)
      ? "Dynamic"
      : null,
  totalAssigned: (report.totalLearners as number) ?? 0,
  totalCompleted: (report.completedCount as number) ?? 0,
  totalInProgress: (report.notStartedCount as number) ?? null,
  statusCode: (report.overallStatus as { code?: string } | null)?.code ?? "",
  statusName: (report.overallStatus as { displayName?: string } | null)?.displayName ?? "",
});

// Custom sort: null values sort to top on ASC
type SortEvent = { data: AssessmentReport[]; order: 1 | 0 | -1 | null | undefined };

const nullFirstSort =
  (field: keyof AssessmentReport, isDate = false) =>
  ({ data, order }: SortEvent): AssessmentReport[] => {
    const o = order ?? 1;
    return [...data].sort((a, b) => {
      if (a == null || b == null) return a == null ? 1 : -1;
      const aVal = a[field] as number | Date | null;
      const bVal = b[field] as number | Date | null;
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return -1 * o;
      if (bVal == null) return 1 * o;
      return (
        (isDate
          ? Date.parse(String(aVal)) - Date.parse(String(bVal))
          : (aVal as number) - (bVal as number)) * o
      );
    });
  };

const STATUS_SORT_ORDER: Record<string, number> = {
  CODE_NOT_STARTED: 0,
  notStarted: 0,
  CODE_STARTED: 1,
  inProgress: 1,
  CODE_OVERDUE: 2,
  overdue: 2,
  CODE_COMPLETED: 3,
  complete: 3,
  CODE_ARCHIVED: 4,
  archived: 4,
};

const statusSort = ({ data, order }: SortEvent): AssessmentReport[] => {
  const o = order ?? 1;
  return [...data].sort((a, b) => {
    if (a == null || b == null) return a == null ? 1 : -1;
    if (a.selfInitiated && !b.selfInitiated) return -1 * o;
    if (!a.selfInitiated && b.selfInitiated) return 1 * o;
    const aOrder = STATUS_SORT_ORDER[a.statusCode] ?? 99;
    const bOrder = STATUS_SORT_ORDER[b.statusCode] ?? 99;
    return (aOrder - bOrder) * o;
  });
};

const endDateSort = ({ data, order }: SortEvent): AssessmentReport[] => {
  const o = order ?? 1;
  const nullRows = data.filter(item => item == null);
  const validRows = data.filter(item => item != null);
  const sorted = [...validRows].sort((a, b) => {
    const aVal = a.endDate;
    const bVal = b.endDate;
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return -1 * o;
    if (bVal == null) return 1 * o;
    if (aVal === "Dynamic" && bVal !== "Dynamic") return o;
    if (aVal !== "Dynamic" && bVal === "Dynamic") return -o;
    if (aVal === "Dynamic" || bVal === "Dynamic") return 0;
    return (Date.parse(String(aVal)) - Date.parse(String(bVal))) * o;
  });
  return [...sorted, ...nullRows];
};

const ASSESSMENT_REPORTS_ROUTE = AdminAppRoutes.routes.reporting.routes.assessments.route;
const SELF_INITIATED_PATH = `${ASSESSMENT_REPORTS_ROUTE}/self-initiated`;

// ---------------------------------------------------------------------------
// Module-level column body templates (no runtime dependencies)
// ---------------------------------------------------------------------------

const titleBody = withSkeleton<AssessmentReport>(row => {
  const to = row.selfInitiated
    ? (row.selfInitiatedPath ?? SELF_INITIATED_PATH)
    : `${ASSESSMENT_REPORTS_ROUTE}/${row.id}`;
  return (
    <Link to={to} className={`flex items-center gap-2 ${themeClasses.textPrimaryDarker}`}>
      {row.selfInitiated && <i className="pi pi-user" />}
      {row.title}
    </Link>
  );
});

const assessmentStatusBody = statusBody<AssessmentReport>(row => {
  if (row.statusCode) {
    const statusKey = STATUS_CODE_MAP[row.statusCode] as KaStatusKey | undefined;
    const config = statusKey ? STATUS_CONFIG[statusKey] : { icon: "", color: "" };
    const colorClass = config.color === "warning" ? themeClasses.textWarning : config.color;
    const label = row.statusName ?? row.statusCode ?? "--";
    const showIcon = config.icon && !row.selfInitiated;
    return {
      icon: showIcon ? config.icon : undefined,
      colorClass: colorClass || undefined,
      label,
    };
  }
  return { label: row.statusName };
});

const startDateBody = withSkeleton<AssessmentReport>(row => {
  if (row.selfInitiated) return "-";
  return humanDateWithoutTime(row.startDate);
});

const endDateBody = withSkeleton<AssessmentReport>(row => {
  if (row.selfInitiated) return "-";
  if (row.endDate === "Dynamic") return "Dynamic";
  return humanEndDate(row.endDate);
});

const completedBody = withSkeleton<AssessmentReport>(row => {
  const { totalCompleted, totalAssigned, id } = row;
  return totalCompleted > 0 ? (
    <Link
      to={`${ASSESSMENT_REPORTS_ROUTE}/${id}?statusFilter=complete`}
      className={themeClasses.textPrimaryDarker}
    >
      {totalCompleted}/{totalAssigned}
    </Link>
  ) : (
    `${totalCompleted}/${totalAssigned}`
  );
});

const notStartedBody = withSkeleton<AssessmentReport>(row => {
  if (row.selfInitiated) return "-";
  const { id, totalAssigned, totalInProgress } = row;
  return (
    <Link
      to={`${ASSESSMENT_REPORTS_ROUTE}/${id}?statusFilter=notStarted`}
      className={themeClasses.textPrimaryDarker}
    >
      {totalInProgress}/{totalAssigned}
    </Link>
  );
});

const listTextFilterElement = textFilterElement("Search...");

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssessmentsReport() {
  useBreadcrumb({
    label: "Assessments Reporting",
    route: ASSESSMENT_REPORTS_ROUTE,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const selfInitiatedEnabled = useSelfInitiatedAssessments();
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const currentTenantName = getCurrentSelectedTenant() ?? "";

  const [state, setState] = useState<AssessmentsReportState>({
    assessments: "loading",
    statusOptions: [],
    categoryScores: [],
    topicScores: {},
    isCategoriesLoading: true,
  });

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeValue>("all");
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
    title: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
    statusCode: { value: null as string[] | null, matchMode: FilterMatchMode.IN },
    startDate: { value: null as [Date, Date] | null, matchMode: FilterMatchMode.BETWEEN },
    endDate: { value: null as [Date, Date] | null, matchMode: FilterMatchMode.BETWEEN },
  });

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    if (!currentTenantName) return;

    // Load statuses (non-blocking)
    getChoresClient()
      .getAvailableStatuses()
      .then(res => {
        if (!res.error && res.data) {
          setState(prev => ({
            ...prev,
            statusOptions: (res.data as { items?: StatusOption[] }).items ?? [],
          }));
        }
      })
      .catch(err => console.error("Failed to load statuses:", err));

    // Load category scores
    getKnowledgeAssessmentReportClient()
      .getScores(currentTenantName, { examKey: "the-exam" })
      .then(response => {
        if (response.error) throw response.error;
        const learnerScores = (response.data?.assignmentScores ?? []).flatMap(
          a => (a.learnerScores ?? []) as LearnerScore[]
        );
        const filtered = filterAllScores(learnerScores);
        const { categoryScoresAverage, topicScoresAverage } = calculateAverageScores(filtered);
        setState(prev => ({
          ...prev,
          categoryScores: categoryScoresAverage,
          topicScores: topicScoresAverage,
          isCategoriesLoading: false,
        }));
      })
      .catch(err => {
        console.error("Failed to load category data:", err);
        showToast({ severity: "error", detail: "Failed to load category data" });
        setState(prev => ({ ...prev, isCategoriesLoading: false }));
      });

    // Load assessments
    try {
      const promises: Promise<unknown>[] = [getChoresClient().getAssessments(currentTenantName)];

      if (selfInitiatedEnabled) {
        promises.push(
          getKnowledgeAssessmentReportClient().getScores(currentTenantName, {
            examKey: "the-exam",
            userInitiated: true,
          })
        );
      }

      const [assignedResult, selfInitiatedResult] = await Promise.all(promises);

      const choresResult = assignedResult as { data?: { reports?: unknown[] }; error?: unknown };
      if (choresResult.error) throw choresResult.error;

      const rawReports = choresResult.data?.reports ?? [];
      const mappedReports: AssessmentReport[] = rawReports.map(r =>
        mapAssessmentToReport(r as Record<string, unknown>)
      );

      if (selfInitiatedEnabled && selfInitiatedResult) {
        const siRes = selfInitiatedResult as {
          data?: { assignmentScores?: Record<string, unknown>[] };
          error?: unknown;
        };
        if (!siRes.error && siRes.data) {
          const allLearners = (siRes.data.assignmentScores ?? []).flatMap(
            a => (a.learnerScores as Record<string, unknown>[]) ?? []
          );
          const completedCount = allLearners.filter(
            l => l.status === "submitted" || l.completedAt
          ).length;
          mappedReports.push({
            id: "self-initiated",
            title: "Self-initiated Assessments",
            startDate: null,
            endDate: null,
            totalAssigned: allLearners.length,
            totalCompleted: completedCount,
            totalInProgress: null,
            statusCode: "CODE_STARTED",
            statusName: "Ongoing",
            selfInitiated: true,
            selfInitiatedPath: SELF_INITIATED_PATH,
          });
        }
      }

      setState(prev => ({ ...prev, assessments: mappedReports }));
    } catch (error) {
      console.error("Failed to load assessments:", error);
      showToast({ severity: "error", detail: "Failed to load assessments" });
      setState(prev => ({ ...prev, assessments: [] }));
    }
  }, [currentTenantName, selfInitiatedEnabled]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Sync globalFilterValue with restored filter state from localStorage
  useEffect(() => {
    if (filters.global?.value && filters.global.value !== globalFilterValue) {
      setGlobalFilterValue(filters.global.value);
    }
  }, [filters.global?.value]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const isAssessmentsLoading = state.assessments === "loading";

  const filteredReports = useMemo(() => {
    if (state.assessments === "loading") return [];
    const cutoffDate = getDateRangeCutoff(selectedDateRange);
    const selfInitiated = state.assessments.filter(r => r.selfInitiated);
    const rest = state.assessments.filter(r => !r.selfInitiated);
    const filteredRest = cutoffDate
      ? rest.filter(report => report.startDate != null && report.startDate >= cutoffDate)
      : rest;
    return [...selfInitiated, ...filteredRest];
  }, [state.assessments, selectedDateRange]);

  const storedRows = useMemo(() => {
    try {
      const stored = window.localStorage.getItem("assessmentReportList-v2");
      return stored
        ? ((JSON.parse(stored) as { rows?: number }).rows ?? DEFAULT_ROW_COUNT)
        : DEFAULT_ROW_COUNT;
    } catch {
      return DEFAULT_ROW_COUNT;
    }
  }, []);

  const skeletonItems = useMemo(() => skeletonRows<AssessmentReport>(storedRows), [storedRows]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const onGlobalFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, global: { ...prev.global, value } }));
    setGlobalFilterValue(value);
  }, []);

  const onFilterChange = useCallback((e: { filters: unknown }) => {
    setFilters(e.filters as typeof filters);
  }, []);

  const exportCSV = useCallback(() => {
    dt.current?.exportCSV({ selectionOnly: false });
  }, []);

  // ---------------------------------------------------------------------------
  // Filter element templates
  // ---------------------------------------------------------------------------

  const statusFilterElement = useMemo(
    () =>
      multiSelectFilterElement(
        state.statusOptions.map(s => ({ label: s.displayName, value: s.code })),
        "Select Status"
      ),
    [state.statusOptions]
  );

  const dateFilterElement = useCallback(
    (options: {
      value: [Date, Date] | null;
      filterApplyCallback: (v: [Date, Date] | null) => void;
    }) => (
      <div className="mt-4">
        <Calendar
          readOnlyInput
          value={options.value}
          onChange={e => options.filterApplyCallback(e.value as [Date, Date] | null)}
          selectionMode="range"
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          appendTo="self"
          hideOnRangeSelection
          showButtonBar
        />
      </div>
    ),
    []
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="mb-4">
        <h1>All Assessments</h1>
      </div>

      <CategoriesCarousel
        categoryScores={state.categoryScores}
        topicScores={state.topicScores}
        isLoading={state.isCategoriesLoading}
      />

      <div>
        {/* Toolbar */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <div className="flex gap-2 items-center">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Search assessments..."
                data-testid="assessments-search-input"
              />
            </IconField>
            <Dropdown
              value={selectedDateRange}
              options={[...DATE_RANGE_OPTIONS]}
              onChange={e => setSelectedDateRange(e.value as DateRangeValue)}
              placeholder="Select time range"
              className="w-48"
            />
          </div>
          <Button
            label="Export data"
            icon="pi pi-download"
            onClick={exportCSV}
            disabled={isAssessmentsLoading}
            data-testid="assessments-export-button"
          />
        </div>

        {/* DataTable */}
        <DataTable
          ref={dt}
          aria-label="Assessment reports"
          data-testid="assessments-report-table"
          value={
            (isAssessmentsLoading
              ? skeletonItems
              : filteredReports) as unknown as DataTableValueArray
          }
          filters={isAssessmentsLoading ? {} : filters}
          onFilter={onFilterChange}
          globalFilterFields={isAssessmentsLoading ? [] : ["title"]}
          stateKey={`assessmentReportList-v2-${currentTenantName}`}
          stateStorage="local"
          paginator
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          emptyMessage="No assessments found"
          removableSort
          tableClassName="dt-responsive-text"
        >
          <Column
            field="title"
            header="Assessment"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={listTextFilterElement}
            body={titleBody}
            style={{ width: "450px" }}
          />
          <Column
            field="statusCode"
            header="Status"
            sortable
            sortFunction={statusSort as unknown as (e: ColumnSortEvent) => void}
            filter
            showFilterMatchModes={false}
            filterElement={statusFilterElement}
            body={assessmentStatusBody}
            key={`status-filter-${state.statusOptions.length}`}
          />
          <Column
            field="startDate"
            header="Start date"
            sortable
            sortFunction={
              nullFirstSort("startDate", true) as unknown as (e: ColumnSortEvent) => void
            }
            filter
            filterMatchMode="between"
            showFilterMatchModes={false}
            showApplyButton={false}
            showClearButton={false}
            filterElement={dateFilterElement}
            body={startDateBody}
          />
          <Column
            field="endDate"
            header="End date"
            sortable
            sortFunction={endDateSort as unknown as (e: ColumnSortEvent) => void}
            filter
            filterMatchMode="between"
            showFilterMatchModes={false}
            showApplyButton={false}
            showClearButton={false}
            filterElement={dateFilterElement}
            body={endDateBody}
          />
          <Column field="totalCompleted" header="Complete" sortable body={completedBody} />
          <Column
            field="totalInProgress"
            header="Not started"
            sortable
            sortFunction={
              nullFirstSort("totalInProgress") as unknown as (e: ColumnSortEvent) => void
            }
            body={notStartedBody}
          />
        </DataTable>
      </div>
    </>
  );
}
